import angular from 'angular'
import debounce from 'lodash/debounce'
import escape from 'lodash/escape'
import startsWith from 'lodash/startsWith'

import KEY from '../../constants/vd-key-codes'

const DEFAULT_WAIT_TIME = 100
const DEFAULT_MAX_WAIT_TIME = 300

/**
 * Abstract controller for a component that contains a text input with the class `vd-autocomplete-input` that is used to
 * search/filter some data using a VdSuggestionSource. Meant to be extended, this provides the common behaviour for
 * the Autocomplete component and the Dropdown component's popover.
 *
 * @class AutocompleteInputController
 */
export default class AutocompleteInputController {
  constructor({ $q, $scope, $element, $timeout, $window, $log }) {
    this._$q = $q
    this._$scope = $scope
    this._$element = $element
    this._$timeout = $timeout
    this._$window = $window
    this._$log = $log

    this._queuedSearches = []

    this._suggestionListOpen = false
    this.data = {
      query: '',
      suggestions: [],
      activeIndex: 0,
      activeFooter: false,
      selected: null,
    }
  }

  $onInit() {
    this._$scope.$watch('$ctrl.data.query', (newValue, oldValue) => {
      this._onQueryChange(newValue, oldValue)
    })

    // Use the passed in custom debounceOptions to decide the wait time of search request
    // If debounceOptions is not passed in, then use the default wait time
    this.debounceOptions = this.debounceOptions || {}
    const { wait = 0, maxWait = 0 } = this.debounceOptions
    const waitTime = wait || DEFAULT_WAIT_TIME
    const maxWaitTime = maxWait || DEFAULT_MAX_WAIT_TIME
    this._search = debounce(query => this._searchSource(query), waitTime, {
      maxWait: maxWaitTime,
    })
  }

  $postLink() {
    this._$scope.$applyAsync(() => {
      this._searchInput = angular.element(
        this._$element[0].querySelector('.vd-autocomplete-input')
      )

      this._typeaheadElement = angular.element(
        '<div class="vd-autocomplete-input-typeahead"></div>'
      )
      this._searchInput.after(this._typeaheadElement)

      this._initSearchInput()
    })

    this._setIcon()
  }

  // Select & Create

  /**
   * Select the given Suggestion.
   *
   * @method select
   * @param  {Suggestion} suggestion
   *
   * @return {Promise} A promise that is resolved if the selection was successful or rejected if the selection
   *         was cancelled/unsuccessful.
   */
  select(suggestion) {
    if (!this._isValidSelection(suggestion)) {
      return this._$q.reject()
    }

    const result = this.source.select({ suggestion })

    if (angular.isUndefined(result)) {
      return this._$q.reject()
    }

    if (result && angular.isFunction(result.then)) {
      return this._selectAsync(result)
    }

    if (!this._isValidSelection(result)) {
      return this._$q.reject()
    }

    return this._$q.resolve(this._select(result))
  }

  /**
   * Determine whether the given suggestion is a valid selection.
   *
   * @protected
   * @method _isValidSelection
   *
   * @param  {Suggestion|null} suggestion
   *         The selected suggestion (null if the current selection is being cleared or the "no selection" suggestion
   *         is selected)
   *
   * @return {Boolean} true if the given suggestion is a valid selection, false otherwise
   */
  _isValidSelection(suggestion) {
    if (suggestion) {
      return true
    }

    return Boolean(
      suggestion === null && (this.source.noSelectionLabel || this.allowClear)
    )
  }

  /**
   * Select the given Suggestion asynchronously. Will invoke `_select` with the suggestion that the given promise
   * resolves to.
   *
   * @protected
   * @method _selectAsync
   *
   * @param  {Promise<Suggestion>} promiseForSuggestion
   *         A promise that will resolve to the suggestion to select (or reject or resolve to undefined if the
   *         selection is cancelled)
   *
   * @return {Promise} A promise that is resolved if the selection was successful or rejected if the selection
   *         was cancelled/unsuccessful.
   */
  _selectAsync(promiseForSuggestion) {
    return promiseForSuggestion.then(suggestion => {
      if (!this._isValidSelection(suggestion)) {
        return this._$q.reject()
      }

      this._select(suggestion)
    })
  }

  /**
   * Select the given Suggestion.
   *
   * @protected
   * @method _select
   *
   * @param  {Suggestion} suggestion
   */
  _select(suggestion) {
    this.data.selected = suggestion
  }

  /**
   * Trigger the behaviour of the currently displayed footer action (clear selection or create new item).
   *
   * @protected
   * @method _triggerFooter
   */
  _triggerFooter() {
    if (this._canClear()) {
      this._doClear()
    } else {
      this._doCreate()
    }
  }

  /**
   * Whether or not the user can clear the current selected value.
   *
   * @protected
   * @method _canClear
   * @return {Boolean}
   */
  _canClear() {
    return this.allowClear && this.source.canClear(this.data)
  }

  /**
   * Clear the currently selected value.
   *
   * @protected
   * @method _doClear
   */
  _doClear() {
    this.select(null)
  }

  /**
   * Trigger the create workflow on the VdSuggestionSource with the current query and select the resulting
   * suggestion if one is successfully created.
   *
   * @protected
   * @method _doCreate
   */
  _doCreate() {
    this.source.create({ query: this.data.query }).then(result => {
      if (result) {
        this.select(result)
      }
    })
  }

  // Searching & Suggestions

  /**
   * Initiliase the search input field, binding keybindings etc.
   *
   * @protected
   * @method _initSearchInput
   */
  _initSearchInput() {
    this._searchInput.on('keydown', event => this._onKeyDown(event))
    this._positionTypeaheadHint()
  }

  /**
   * Position the element responsible for displaying the typeahead hint so that the hint displays correctly after
   * the current search query text.
   *
   * @protected
   * @method _positionTypeaheadHint
   */
  _positionTypeaheadHint() {
    if (!this._searchInput || !this._typeaheadElement) {
      return
    }

    const left = this._searchInput[0].offsetLeft
    const top = this._searchInput[0].offsetTop
    const { borderWidth, paddingRight } = this._$window.getComputedStyle(
      this._searchInput[0]
    )

    // We use calc() here because borderWidth will be a string like "1px" and so its safer to do the calc in CSS than
    // attempt to parse it and do the math here in JS
    this._typeaheadElement.css({
      top: `calc(${top}px + ${borderWidth})`,
      left: `calc(${left}px + ${borderWidth})`,
      // Ensure width of the typeahead hint never exceeds the available width inside the input container
      width: `calc(100% - (${left}px + ${paddingRight} + (${borderWidth} * 2)))`,
    })
  }

  /**
   * Handle the search query changing.
   *
   * @protected
   * @method _onQueryChange
   *
   * @param  {String} query The current value of the query
   * @param  {String} prevQuery The previous value of the query
   */
  _onQueryChange(query, prevQuery) {
    this._search(query)

    if (query !== prevQuery) {
      this._setTypeaheadHint()
    }
  }

  /**
   * Search the VdSuggestionSource for the given query string and update the current list of suggestions with those
   * returned by the search.
   *
   * @protected
   * @method _searchSource
   *
   * @param  {String} query
   */
  _searchSource(query) {
    // The $timeout ensures that a barcode scan will trigger feelingLucky()
    // before we get any search suggestions back to show
    const promiseForSuggestions = this._$timeout(() => {
      return this.source
        .search({ query })
        .then(data => {
          const index = this._queuedSearches.indexOf(promiseForSuggestions)
          if (index < 0) {
            return
          }

          this._updateSuggestions(data)
          this._queuedSearches.splice(0, index + 1)
        })
        .catch(error => this._handleSearchSourceError(error, query))
    }, 0)

    this._queuedSearches.push(promiseForSuggestions)
  }

  /**
   * Update the current list of suggestions to the given suggestions.
   *
   * @protected
   * @method _updateSuggestions
   *
   * @param  {Object} data
   *         @param {Array<Suggestion>} data.suggestions
   *                The list of suggestions found by the latest search
   *         @param {Array<Group>} data.groups
   *                The collection of suggestions grouped into groups (to support groupBy)
   */
  _updateSuggestions({ suggestions, groups }) {
    this.data.suggestions = suggestions
    this.data.groups = groups
    this.data.activeIndex = 0

    if (this.source.create) {
      this.data.activeFooter =
        !this.data.suggestions.length && this.source.allowFeelingLuckyCreate
    }

    this._$scope.$applyAsync(() => this._setTypeaheadHint())
  }

  /**
   * Handle an error occurring while searching the source.
   *
   * @protected
   * @method _handleSearchSourceError
   *
   * @param  {Error} error The error that occurred
   * @param  {String} query The query that was searched for when the error occurred
   */
  _handleSearchSourceError(error, query) {
    this._$log.error('[VD-AUTOCOMPLETE-INPUT] Error Searching Source', {
      error,
      query,
    })
  }

  /**
   * Cancel the current queue of searches (e.g. because the user closed the suggestion list or made a selection).
   *
   * @protected
   * @method _cancelQueue
   */
  _cancelQueue() {
    // Clear queue for suggestions so we don't re-show the suggestions if there's still a search in progress
    this._queuedSearches = []
    // Cancel any pending debounced calls to _search()
    this._search.cancel()
  }

  // Keyboard Interaction

  /**
   * Event listener for a keydown event on the search input. Starts a digest and invokes _handleKeyDown.
   *
   * @protected
   * @method _onKeyDown
   *
   * @param  {Event} event
   */
  _onKeyDown(event) {
    const key = event.which

    if (
      key === KEY.ENTER ||
      (this._suggestionListOpen &&
        [KEY.TAB, KEY.UP, KEY.DOWN].indexOf(key) > -1)
    ) {
      event.preventDefault()
      event.stopPropagation()
    }

    this._$scope.$apply(() => this._handleKeyDown(key, event))
  }

  /**
   * Handle a keydown event on the search input. Interprets Up/Down/Tab/Shift+Tab as moving up and down the list of
   * suggestions and Enter as selecting the current highlighted suggestion/footer.
   *
   * @protected
   * @method _handleKeyDown
   *
   * @param  {Number} key The keycode of the key that was pressed
   * @param  {Event} event The original keydown event object
   */
  _handleKeyDown(key, event) {
    switch (key) {
      case KEY.TAB:
        if (event.shiftKey) {
          this._activatePreviousSuggestion()
        } else {
          this._activateNextSuggestion()
        }
        break
      case KEY.DOWN:
        this._activateNextSuggestion()
        break
      case KEY.UP:
        this._activatePreviousSuggestion()
        break
      case KEY.ENTER:
        if (this._suggestionListOpen) {
          if (this.data.activeFooter) {
            this.data.activeFooter = false
            this._triggerFooter()
          } else if (this.data.suggestions.length) {
            this.select(this.data.suggestions[this.data.activeIndex])
          }
        }
        break
    }

    this._setTypeaheadHint()
  }

  /**
   * Activate (highlight) the next suggestion in the list or the create/clear footer if it is present and we're at
   * the bottom of the list.
   *
   * @protected
   * @method _activateNextSuggestion
   */
  _activateNextSuggestion() {
    const maxIndex = this.data.suggestions.length - 1
    if (this._suggestionListOpen) {
      if (this.data.activeIndex < maxIndex) {
        this.data.activeIndex += 1
      } else if (this.source.canCreate(this.data) || this._canClear()) {
        this.data.activeFooter = true
      }
    }
  }

  /**
   * Activate (highlight) the previous suggestion in the list.
   *
   * @protected
   * @method _activatePreviousSuggestion
   */
  _activatePreviousSuggestion() {
    if (this._suggestionListOpen) {
      if (this.data.activeFooter) {
        this.data.activeFooter = false
      } else if (this.data.activeIndex > 0) {
        this.data.activeIndex -= 1
      }
    }
  }

  /**
   * Set the typeahead hint to the remaining text of the currently active suggestion if there is an active suggestion
   * that starts with the current search query, otherwise clears the hint.
   *
   * @protected
   * @method _setTypeaheadHint
   */
  _setTypeaheadHint() {
    const query = this.data.query
    let typeaheadContent = ''

    if (this._suggestionListOpen && query && !this.data.activeFooter) {
      const activeSuggestion = this.data.suggestions[this.data.activeIndex]
      const displayText = this.source.getDisplayText(activeSuggestion)

      if (
        displayText &&
        startsWith(displayText.toLowerCase(), query.toLowerCase())
      ) {
        const typeahead = escape(displayText.substring(query.length))
        typeaheadContent = `<span class="vd-autocomplete-input-typeahead-match">${escape(
          query
        )}</span>${typeahead}`
      }
    }

    this._typeaheadElement.html(typeaheadContent)
  }

  /**
   * Set the icon to display inside the search input.
   *
   * @protected
   * @method _setIcon
   */
  _setIcon() {
    if (!this.icon) {
      return
    }

    this.inputSymbol = { align: 'left', icon: this.icon }
  }
}
